/**
 * crmToday — the unified daily worklist merging Leads + Opportunities into one urgency-sorted
 * queue, reusing the SAME two rule engines (crmLeadNextStep, crmOpportunityNextStep) that already
 * drive each entity's own detail modal. This is Phase 2 of the "always-on engine" work — Tier 1
 * shipped the per-record engines and cheap list-tier sorting; this is the "someone manning the
 * shop opens this every morning" surface built on top of them.
 *
 * Cost note: unlike CrmLeadsView/CrmOpportunitiesView's cheap list-tier heuristics, this queue
 * runs the FULL rule engines (real interactions per record, real countersign status where
 * relevant) since it's meant to be the authoritative daily worklist, not just a sort key. That's
 * one or two extra reads per active record — the existing `_api.js` concurrency-capped queue
 * (3 at a time, 429 backoff-retry) absorbs this safely; for a firm this size (a few dozen active
 * leads+opportunities at once) this is a few seconds on open, not a rate-limit problem.
 */
import { getLeads } from './leads.js'
import { getOpportunities } from './opportunities.js'
import { getInteractionsForLead, getInteractionsForOpportunity } from './interactions.js'
import { getProposalsForOpportunity, getCountersignStatus } from './proposals.js'
import { getLeadJudgment } from './leadJudgment.js'
import { getLeadQualification } from './leadQualification.js'
import { leadNextStep } from '../components/crm/crmLeadNextStep.js'
import { opportunityNextStep } from '../components/crm/crmOpportunityNextStep.js'

const RANK = { red: 0, amber: 1, teal: 2, gray: 3 }
// Terminal steps — showing these in a daily worklist would just be noise. 'not-a-client' is
// deliberately NOT excluded anymore (2026-08): an open not-a-client lead has a real pending
// action — close it out — and hiding it here was how those leads sat in "New" forever.
const EXCLUDE_STEPS = new Set(['converted', 'disqualified', 'closed', 'won'])

/**
 * Returns [{ type: 'lead'|'opportunity', id, record, step }], most urgent first. `record` is the
 * normalized lead/opportunity object from its own service (services/leads.js / opportunities.js);
 * `step` is that record's own leadNextStep/opportunityNextStep result.
 */
export async function getTodayQueue() {
  const [leads, opps] = await Promise.all([getLeads(), getOpportunities()])
  const activeLeads = leads.filter(l => l.status !== 'Disqualified' && !l.converted)

  const leadEntries = await Promise.all(activeLeads.map(async lead => {
    const interactions = await getInteractionsForLead(lead.id).catch(() => [])
    const judgment = await getLeadJudgment(lead.id).catch(() => null)
    const qualification = await getLeadQualification(lead.id).catch(() => null)
    return { type: 'lead', id: lead.id, record: lead, step: leadNextStep(lead, interactions, judgment, qualification) }
  }))

  const oppEntries = await Promise.all(opps.map(async opp => {
    const interactions = await getInteractionsForOpportunity(opp.id).catch(() => [])
    let countersignStatus = null
    if (opp.stage === 'Proposal Posted' || opp.stage === 'Pending Countersignature') {
      try {
        const proposals = await getProposalsForOpportunity(opp.id)
        const active = (proposals || []).find(p => p.status === 'Posted' || p.status === 'Pending')
        if (active && active.status === 'Pending') countersignStatus = await getCountersignStatus(active.id)
      } catch (_) { /* best-effort — the engine handles a missing countersignStatus fine */ }
    }
    return { type: 'opportunity', id: opp.id, record: opp, step: opportunityNextStep(opp, interactions, countersignStatus) }
  }))

  return [...leadEntries, ...oppEntries]
    .filter(e => !EXCLUDE_STEPS.has(e.step.step))
    .sort((a, b) => (RANK[a.step.urgency] ?? 9) - (RANK[b.step.urgency] ?? 9))
}
